import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import cx from 'clsx';

import { SUPPORTED_LOCALE_TYPE } from '@/locales';
import { Button, Checkbox } from '@/components';
import { useSyncI18n } from '@/utils';

import { SetGroupEligibilities } from './fields';
import style from './index.module.scss';

// 表单内容，{ [key]: 以下 }
// 1. 只有一个 checkbox：boolean
// 2. 有前面的 checkbox，且有一个 input：[boolean, string]

type FormData = Record<string, boolean | [boolean, number | string | Object]>;

export const SetGroupEligibilityPanel = ({
  disabled,
  onSubmit,
  initEligibility,
  language,
}: {
  language?: SUPPORTED_LOCALE_TYPE;
  disabled?: boolean;
  initEligibility?: Record<string, null | number | Object>;
  onSubmit?(arg: Record<string, null | number | Object>): void;
}) => {
  const { t } = useTranslation();

  useSyncI18n(language);

  const defaultEligibility = useMemo(() => {
    return Object.entries(initEligibility || {}).reduce((m, [k, v]) => {
      if (v === null) {
        m[k] = true;
      } else {
        m[k] = [true, v];
      }
      return m;
    }, {} as FormData);
  }, [initEligibility]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultEligibility,
  });

  useEffect(() => {
    reset(defaultEligibility);
  }, [defaultEligibility, reset]);

  const restrictions = useWatch({ control });

  const restrictionsCount = useMemo(() => {
    return restrictions
      ? Object.keys(restrictions).filter(x => {
          const v = restrictions[x];
          return Array.isArray(v) ? !!v[0] && !!v[1] : v;
        }).length
      : 0;
  }, [restrictions]);

  const handleSubmitClick = useCallback(
    (data: FormData) => {
      const next = Object.entries(data).reduce((m, [k, v]) => {
        if (Array.isArray(v)) {
          if (v[0] && !!v[1]) {
            m[k] = k !== 'has_one_of_badges' ? Number(v[1]) : v[1];
          }
        } else if (!!v) {
          m[k] = null;
        }

        return m;
      }, {} as any);
      onSubmit?.(next);
    },
    [onSubmit],
  );

  return (
    <div className={style.modalContainer}>
      <div className={style.title}>
        {t('SetGroupEligibilityPanel.1f1r1rs')}
        <span className={style.eligiblityCount}>
          (
          {restrictionsCount
            ? t('SetGroupEligibilityPanel.restrict', { count: restrictionsCount })
            : t('SetGroupEligibilityPanel.noRestriction')}
          )
        </span>
      </div>
      <div className={style.formContainer}>
        {SetGroupEligibilities.map(m => (
          <div className={style.formField} key={m.name}>
            <div className={cx(style.fieldLabel, m?.labelContainerClassName)}>
              <Controller
                name={m.render ? `${m.name}.0` : `${m.name}`}
                control={control}
                render={({ field, fieldState }) => (
                  <Checkbox
                    className={style.checkbox}
                    onChange={field.onChange}
                    checked={field.value as boolean}
                    disabled={disabled}>
                    <span className={cx(style.fieldLabelText)}>{m.label}</span>
                  </Checkbox>
                )}
              />
            </div>
            {m.render?.(control, disabled)}
          </div>
        ))}
      </div>
      <Button
        type="primary"
        disabled={disabled || !isValid}
        className={style.postBtn}
        onClick={handleSubmit(handleSubmitClick)}>
        {t('SetGroupEligibilityPanel.mf1qqm')}
      </Button>
    </div>
  );
};

export { SetGroupEligibilities } from './fields';
